import { fromOklab, isHexColor, isNamedColor, resolveColor, toOklab } from './colors';

/**
 * Gradients the way Minecraft can show them: one solid colour per character.
 *
 * Chat has no gradient primitive, so a gradient becomes a row of coloured
 * pieces. Three choices keep that row faithful and small:
 *
 * - A "character" is a grapheme (what a reader counts as one letter), so an
 *   emoji with a skin tone or an accented letter built from two code points
 *   takes one colour step instead of splitting into halves of different colour.
 * - Whitespace takes no step. Spaces and line breaks are invisible, so giving
 *   them a colour would stretch the gradient over nothing; they ride along
 *   with the piece before them (or after them, at the start of the text), which
 *   also keeps an underline or strikethrough unbroken across a space.
 * - Neighbouring pieces of the same colour merge, which costs nothing on screen
 *   and keeps the command short.
 *
 * Colours are interpolated in OKLab (see colors.ts) so the steps look even.
 * The output depends only on the input, which the compiler's golden tests and
 * the "same command every time" promise of a schedule both rely on.
 */

interface GraphemeSegmenter {
  segment(input: string): Iterable<{ segment: string }>;
}

type GraphemeSegmenterConstructor = new (
  locales?: string | string[],
  options?: { granularity: 'grapheme' },
) => GraphemeSegmenter;

// The ES2020 lib has no types for Intl.Segmenter, so reach it through a guard.
// The instance is cached per constructor so a test can swap the global out.
let cached: { ctor: unknown; segmenter: GraphemeSegmenter | null } | null = null;

function graphemeSegmenter(): GraphemeSegmenter | null {
  const ctor: unknown = (Intl as unknown as { Segmenter?: unknown }).Segmenter;
  if (typeof ctor !== 'function') return null;
  if (cached?.ctor !== ctor) {
    let segmenter: GraphemeSegmenter | null = null;
    try {
      segmenter = new (ctor as GraphemeSegmenterConstructor)(undefined, { granularity: 'grapheme' });
    } catch {
      segmenter = null;
    }
    cached = { ctor, segmenter };
  }
  return cached.segmenter;
}

/** The text split into user-perceived characters, or code points where the runtime cannot segment. */
export function graphemes(text: string): string[] {
  const segmenter = graphemeSegmenter();
  if (!segmenter) return Array.from(text);
  return Array.from(segmenter.segment(text), part => part.segment);
}

const isAnyColor = (value: unknown) => isNamedColor(value) || isHexColor(value);

/**
 * `count` colours spread evenly across the stops, interpolated in OKLab. The
 * first and last colours are the first and last stops exactly, and a stop that
 * lands on a whole step comes out unchanged. Stops that are not colours are
 * skipped; with none left every step is white.
 */
export function gradientColors(count: number, stops: readonly string[]): string[] {
  const steps = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
  if (steps === 0) return [];
  const colours = stops.filter(isAnyColor).map(stop => resolveColor(stop));
  if (colours.length === 0) return Array<string>(steps).fill('#ffffff');
  if (colours.length === 1 || steps === 1) return Array<string>(steps).fill(colours[0]);
  const labs = colours.map(toOklab);
  const spans = colours.length - 1;
  const result: string[] = [];
  for (let i = 0; i < steps; i += 1) {
    const position = (i / (steps - 1)) * spans;
    const index = Math.min(Math.floor(position), spans - 1);
    const t = position - index;
    if (t < 1e-9) {
      result.push(colours[index]);
    } else if (t > 1 - 1e-9) {
      result.push(colours[index + 1]);
    } else {
      const [from, to] = [labs[index], labs[index + 1]];
      result.push(fromOklab([
        from[0] + (to[0] - from[0]) * t,
        from[1] + (to[1] - from[1]) * t,
        from[2] + (to[2] - from[2]) * t,
      ]));
    }
  }
  return result;
}

const isBlank = (grapheme: string) => /^\s+$/.test(grapheme);

/**
 * The text cut into pieces of one colour each: one gradient step per visible
 * grapheme, whitespace joined to its neighbour, equal neighbours merged.
 * Colours are `#rrggbb`. Text that is all whitespace comes back as one piece
 * in the first stop's colour; empty text comes back empty.
 */
export function applyGradient(text: string, stops: readonly string[]): { text: string; color: string }[] {
  if (!text) return [];
  const parts = graphemes(text);
  const visible = parts.filter(part => !isBlank(part)).length;
  const colours = gradientColors(Math.max(visible, 1), stops);
  const pieces: { text: string; color: string }[] = [];
  let leading = '';
  let step = 0;
  for (const part of parts) {
    const last = pieces[pieces.length - 1];
    if (isBlank(part)) {
      if (last) last.text += part;
      else leading += part;
      continue;
    }
    const color = colours[step];
    step += 1;
    if (last && last.color === color) {
      last.text += part;
    } else {
      pieces.push({ text: leading + part, color });
      leading = '';
    }
  }
  if (leading) pieces.push({ text: leading, color: colours[0] });
  return pieces;
}
